<script setup lang="ts">
import RouteNameEnum from '@/router/routeNameEnum';
import TheHeader from '@/domains/common/components/TheHeader.vue';
import TheNotification from '@/domains/common/components/TheNotification.vue';
import TheLineBackground from '@/domains/common/components/TheLineBackground.vue';
import TheLoader from '@/domains/common/components/TheLoader.vue';
import useAuthStore from '@/domains/authentication/store/auth.store';
import TheVersion from '@/domains/common/components/TheVersion.vue';
import TheGlobalAnnouncement from '@/domains/admin/components/TheGlobalAnnouncement.vue';
import useAdminAnnouncementStore from '@/domains/admin/store/adminAnnouncement.store';
import { isParamString } from '@/router/routeParamType';
import { computed, onMounted } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import '@vuepic/vue-datepicker/dist/main.css';
import { storeToRefs } from 'pinia';

const route = useRoute();

const authStore = useAuthStore();

const adminAnnouncementStore = useAdminAnnouncementStore();

const { isLoadingUser } = storeToRefs(authStore);

const AUTH_LAYOUT_ROUTES = [
    RouteNameEnum.Login,
    RouteNameEnum.ForgottenPassword,
    RouteNameEnum.ResetPassword,
    RouteNameEnum.Invite,
];

// Keying the view on the route id forces a remount when the order or the patient changes:
// without it a direct /order/A/scan -> /order/B/scan navigation would reuse the same view,
// skipping onUnmounted and leaving the previous patient data in place. Correctness relies on
// Vue unmounting the outgoing keyed view before mounting the new one, so wrapping RouterView
// in KeepAlive or Transition would break it.
const routeKey = computed(() => {
    return isParamString(route.params.id) ? route.params.id : undefined;
});

const containerClass = computed(() => {
    if (!AUTH_LAYOUT_ROUTES.includes(route.name?.toString() as RouteNameEnum)) {
        return 'pb-3 h-screen flex flex-col overflow-hidden';
    }

    return '';
});

const isHeaderVisible = computed(() => {
    return !AUTH_LAYOUT_ROUTES.includes(route.name?.toString() as RouteNameEnum) && route.name;
});

const shouldDisplayLineBackground = computed(() => {
    return ![...AUTH_LAYOUT_ROUTES, RouteNameEnum.Scan].includes(route.name?.toString() as RouteNameEnum);
});

const shouldDisplayGlobalAnnouncement = computed(() => {
    return ![...AUTH_LAYOUT_ROUTES, RouteNameEnum.Scan].includes(route.name?.toString() as RouteNameEnum);
});

onMounted(() => {
    adminAnnouncementStore.fetchActivatedAnnouncements();
});
</script>

<template>
    <div :class="[containerClass, 'h-screen']">
        <TheHeader v-if="isHeaderVisible" />
        <TheNotification />
        <TheGlobalAnnouncement v-if="shouldDisplayGlobalAnnouncement" />
        <TheLoader v-if="isLoadingUser" />
        <TheLineBackground v-if="shouldDisplayLineBackground">
            <RouterView v-if="!isLoadingUser" :key="routeKey" />
        </TheLineBackground>

        <template v-else>
            <RouterView v-if="!isLoadingUser" :key="routeKey" />
        </template>
        <TheVersion />
    </div>
</template>
